import type { GeoLayer, GeoZoomTier } from '@pulsify/shared/dto'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { EyeOff, Globe2, Maximize2, Minimize2, Radio, ScanLine, Users } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useAdminGeoActivity } from '~/entities/stats/model/use-admin-stats'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { DURATION } from '~/shared/lib/motion'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Skeleton } from '~/shared/ui/skeleton'
import { GeoMap } from './geo-map'

const WINDOWS = [7, 30, 90] as const
type Window = (typeof WINDOWS)[number]

const WINDOW_LABELS: Record<Window, () => string> = {
  7: m.geoWindow7,
  30: m.geoWindow30,
  90: m.geoWindow90,
}

const LAYERS: readonly { value: GeoLayer; label: () => string }[] = [
  { value: 'usage', label: m.geoLayerUsage },
  { value: 'scans', label: m.geoLayerScans },
  { value: 'live', label: m.geoLayerLive },
]

const TIER_LABELS: Record<GeoZoomTier, () => string> = {
  europe: m.geoTierEurope,
  country: m.geoTierCountry,
  city: m.geoTierCity,
}

/** Floating over the map it needs its own surface; in the card view the Card already is one. */
const FLOATING = 'border border-border/60 bg-card/80 shadow-2xl backdrop-blur-xl'

function Surface({
  floating,
  className,
  children,
}: {
  floating: boolean
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'rounded-xl',
        floating ? `${FLOATING} pointer-events-auto` : 'bg-card border border-border',
        className,
      )}
    >
      {children}
    </div>
  )
}

function Total({
  icon: Icon,
  label,
  value,
  active,
}: {
  icon: typeof Users
  label: string
  value: string
  active?: boolean
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span
        className={cn(
          'flex justify-center items-center rounded-md w-7 h-7 shrink-0',
          active ? 'bg-brand text-brand-foreground' : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon className="w-3.5 h-3.5" aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block font-semibold text-base tabular-nums leading-tight">{value}</span>
        <span className="block text-muted-foreground text-[11px] truncate">{label}</span>
      </span>
    </div>
  )
}

export function GeoPage() {
  const { view } = useSearch({ from: '/admin/map' })
  const navigate = useNavigate()
  const expanded = view === 'expanded'
  const [days, setDays] = useState<Window>(30)
  const [layer, setLayer] = useState<GeoLayer>('usage')
  const [mapStyle, setMapStyle] = useState<'dots' | 'heat'>('dots')
  const [tier, setTier] = useState<GeoZoomTier>('country')
  const query = useAdminGeoActivity({ tier, days })
  const locale = getLocale()
  const count = (value: number) => new Intl.NumberFormat(locale).format(value)

  const cells = query.data?.cells ?? []
  const totals = useMemo(
    () =>
      cells.reduce(
        (sum, cell) => ({
          people: sum.people + cell.people,
          opens: sum.opens + cell.opens,
          scans: sum.scans + cell.scans,
          live: sum.live + cell.live,
        }),
        { people: 0, opens: 0, scans: 0, live: 0 },
      ),
    [cells],
  )

  const toggleView = () =>
    navigate({ to: '/admin/map', search: { view: expanded ? 'card' : 'expanded' } })

  const controls = (
    <>
      <SegmentedControl
        label={m.geoLayerLabel()}
        size="sm"
        value={layer}
        onChange={(value) => setLayer(value)}
        options={LAYERS.map((entry) => ({ value: entry.value, label: entry.label() }))}
      />
      <SegmentedControl
        label={m.geoWindowLabel()}
        size="sm"
        value={String(days)}
        onChange={(value) => setDays(Number(value) as Window)}
        options={WINDOWS.map((window) => ({
          value: String(window),
          label: WINDOW_LABELS[window](),
        }))}
      />
      <SegmentedControl
        label={m.geoStyleLabel()}
        size="sm"
        value={mapStyle}
        onChange={(value) => setMapStyle(value)}
        options={[
          { value: 'dots' as const, label: m.geoStyleDots() },
          { value: 'heat' as const, label: m.geoStyleHeat() },
        ]}
      />
    </>
  )

  const toggle = (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggleView}
      aria-label={expanded ? m.geoCollapse() : m.geoExpand()}
      title={expanded ? m.geoCollapse() : m.geoExpand()}
    >
      {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
    </Button>
  )

  // Keyed on data rather than on the loading flag: once the map exists it must never be
  // unmounted, or every tier change would rebuild it and throw away the viewport mid-gesture.
  const map = query.data ? (
    <GeoMap cells={cells} layer={layer} style={mapStyle} onTierChange={setTier} />
  ) : (
    <Skeleton className="rounded-none w-full h-full" />
  )

  const totalsGrid = (
    <div className="gap-3 grid grid-cols-2 p-3">
      <Total
        icon={Users}
        label={m.geoTotalPeople()}
        value={count(totals.people)}
        active={layer === 'usage'}
      />
      <Total icon={Globe2} label={m.geoTotalOpens()} value={count(totals.opens)} />
      <Total
        icon={ScanLine}
        label={m.geoTotalScans()}
        value={count(totals.scans)}
        active={layer === 'scans'}
      />
      <Total
        icon={Radio}
        label={m.geoTotalLive()}
        value={count(totals.live)}
        active={layer === 'live'}
      />
    </div>
  )

  const cityList = (
    <div className="p-4">
      <h2 className="font-medium text-sm">{m.geoTopCities()}</h2>
      {(query.data?.topCities.length ?? 0) === 0 ? (
        <p className="mt-2 text-muted-foreground text-xs">{m.geoNoCities()}</p>
      ) : (
        <ol className="space-y-1.5 mt-3">
          {query.data?.topCities.map((city, index) => (
            <li key={city.city} className="flex justify-between items-center gap-2 text-sm">
              <span className="flex items-center gap-2 min-w-0">
                <span className="w-4 text-muted-foreground text-xs tabular-nums">{index + 1}</span>
                <span className="truncate">{city.city}</span>
              </span>
              <span className="flex items-center gap-2 text-xs tabular-nums shrink-0">
                <span>{count(city.scans)}</span>
                {city.live > 0 ? (
                  <span className="inline-flex items-center gap-1 text-brand">
                    <Radio className="w-3 h-3" aria-hidden />
                    {count(city.live)}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  )

  const countryList =
    (query.data?.countries.length ?? 0) > 0 ? (
      <div className="p-4">
        <h2 className="font-medium text-sm">{m.geoCountryAccounts()}</h2>
        <ul className="gap-x-4 gap-y-1 grid grid-cols-2 mt-3">
          {query.data?.countries
            .slice()
            .sort((left, right) => right.accounts - left.accounts)
            .slice(0, 8)
            .map((country) => (
              <li key={country.code} className="flex justify-between items-center text-sm">
                <span>{country.code}</span>
                <span className="text-xs tabular-nums">{count(country.accounts)}</span>
              </li>
            ))}
        </ul>
      </div>
    ) : null

  const legend = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 text-muted-foreground text-xs">
      <span className="font-medium text-foreground">{TIER_LABELS[query.data?.tier ?? tier]()}</span>
      {query.data ? (
        <>
          <span>{m.geoCellLegend({ km: count(query.data.cellKm) })}</span>
          <span>{m.geoFloorLegend({ floor: count(query.data.floor) })}</span>
          {query.data.suppressedCells > 0 ? (
            <span className="inline-flex items-center gap-1">
              <EyeOff className="w-3 h-3" aria-hidden />
              {m.geoSuppressedNote({ count: count(query.data.suppressedCells) })}
            </span>
          ) : null}
        </>
      ) : null}
      {query.data && query.data.tier !== query.data.requestedTier ? (
        <span className="text-foreground">{m.geoCoarserTier()}</span>
      ) : (
        <span className="hidden lg:inline opacity-70">{m.geoZoomHint()}</span>
      )}
    </div>
  )

  const emptyNote =
    query.data && cells.length === 0 ? (
      <div className="z-10 absolute inset-0 flex justify-center items-center p-8 pointer-events-none">
        <p className={cn('rounded-xl max-w-sm px-4 py-3 text-sm text-center', FLOATING)}>
          {m.geoEmpty()}
        </p>
      </div>
    ) : null

  if (!expanded) {
    return (
      <div className="px-4 sm:px-8 py-8 sm:py-16">
        <div className="space-y-8 mx-auto max-w-6xl">
          <header className="flex flex-wrap justify-between items-end gap-x-6 gap-y-4">
            <div className="space-y-1.5">
              <h1 className="flex items-center gap-2.5 font-semibold text-foreground text-3xl tracking-tight">
                <Globe2 className="w-7 h-7" aria-hidden />
                {m.geoMapTitle()}
              </h1>
              <p className="text-muted-foreground text-sm">{m.geoMapSubtitle()}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {controls}
              {toggle}
            </div>
          </header>

          <div className="items-start gap-4 grid lg:grid-cols-[1fr_20rem]">
            <Surface floating={false} className="overflow-hidden">
              <div className="relative h-[30rem] lg:h-[34rem]">
                {map}
                {emptyNote}
              </div>
              <div className="border-border border-t">{legend}</div>
            </Surface>

            <div className="space-y-4">
              <Surface floating={false}>{totalsGrid}</Surface>
              <Surface floating={false}>{cityList}</Surface>
              {countryList ? <Surface floating={false}>{countryList}</Surface> : null}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative w-full h-[calc(100dvh-3.5rem)] lg:h-dvh overflow-hidden">
      <div className="z-0 absolute inset-0">{map}</div>

      <div
        style={{ transitionDuration: `${DURATION.slow}ms` }}
        className="z-10 absolute inset-0 flex flex-col justify-between gap-3 p-3 lg:p-4 lg:pr-[20rem] lg:pl-[var(--admin-rail-inset)] pointer-events-none motion-safe:transition-[padding] motion-safe:ease-out"
      >
        <Surface floating className="flex items-center gap-2 p-1.5 pl-3 max-w-full lg:w-fit">
          <h1 className="flex items-center gap-2 font-semibold text-sm whitespace-nowrap shrink-0">
            <Globe2 className="w-4 h-4" aria-hidden />
            <span className="hidden sm:inline">{m.geoMapTitle()}</span>
          </h1>
          <div className="flex items-center gap-1.5 min-w-0 overflow-x-auto [scrollbar-width:none]">
            {controls}
          </div>
          {toggle}
        </Surface>

        <Surface floating className="lg:hidden">
          <div className="gap-2 grid grid-cols-4 px-3 py-2.5">
            {[
              { label: m.geoTotalPeople(), value: totals.people, active: layer === 'usage' },
              { label: m.geoTotalOpens(), value: totals.opens, active: false },
              { label: m.geoTotalScans(), value: totals.scans, active: layer === 'scans' },
              { label: m.geoTotalLive(), value: totals.live, active: layer === 'live' },
            ].map((total) => (
              <div key={total.label} className="min-w-0 text-center">
                <span
                  className={cn(
                    'block font-semibold text-sm tabular-nums leading-tight',
                    total.active && 'text-brand',
                  )}
                >
                  {count(total.value)}
                </span>
                <span className="block text-[10px] text-muted-foreground truncate">
                  {total.label}
                </span>
              </div>
            ))}
          </div>
        </Surface>

        <Surface floating className="hidden lg:block w-fit max-w-full">
          {legend}
        </Surface>
      </div>

      <div className="hidden lg:flex z-10 absolute inset-y-4 right-4 flex-col w-[18rem] pointer-events-none">
        <Surface
          floating
          className="flex flex-col divide-y divide-border/60 max-h-full overflow-y-auto"
        >
          {totalsGrid}
          {cityList}
          {countryList}
        </Surface>
      </div>

      {emptyNote}
    </div>
  )
}
